import React, { useState, useMemo } from 'react';
import Icon from '@salesforce/design-system-react/components/icon';
import { colors, type, space, radius, formatMoney, relativeDate } from '../tokens';
import { timelineIcon } from '../icons';
import TimelineFilterChips from './TimelineFilterChips';

const DAY_MS = 86400000;

const dayBucket = (d) => {
  const dt = typeof d === 'string' ? new Date(d) : d;
  dt.setHours(0, 0, 0, 0);
  return dt.toISOString().slice(0, 10);
};

const bucketLabel = (isoDay) => {
  const dt = new Date(isoDay);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - dt.getTime()) / DAY_MS);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return 'Last week';
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const EventIcon = ({ type: evType }) => {
  const cfg = timelineIcon[evType] || timelineIcon.system;
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: `${cfg.color}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: `1.5px solid ${cfg.color}`,
      }}
    >
      <Icon category="utility" name={cfg.name} size="x-small" style={{ fill: cfg.color }} />
    </div>
  );
};

const SingleItem = ({ event }) => (
  <div style={{ display: 'flex', gap: space[3], alignItems: 'flex-start' }}>
    <EventIcon type={event.type} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: space[2], alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ ...type.body, fontWeight: 600, color: colors.textPrimary }}>
          {event.title}
        </span>
        {event.amount != null && event.type === 'gift' && (
          <span style={{
            ...type.small,
            fontWeight: 700,
            color: colors.timeline.gift,
          }}>
            {formatMoney(event.amount)}
          </span>
        )}
        {event.auto && (
          <span style={{
            ...type.micro,
            color: colors.textTertiary,
            padding: '1px 6px',
            border: `1px solid ${colors.border}`,
            borderRadius: radius.pill,
          }}>
            auto
          </span>
        )}
      </div>
      {event.description && (
        <div style={{ ...type.small, color: colors.textSecondary, marginTop: 2 }}>
          {event.description}
        </div>
      )}
      <div style={{ ...type.micro, color: colors.textTertiary, marginTop: 2 }}>
        {relativeDate(event.date)}
        {event.actor && event.actor !== 'System' && ` · by ${event.actor}`}
      </div>
    </div>
  </div>
);

const CollapsedGroup = ({ events, onExpand }) => {
  const cfg = timelineIcon[events[0].type] || timelineIcon.system;
  return (
    <div style={{ display: 'flex', gap: space[3], alignItems: 'flex-start' }}>
      <EventIcon type={events[0].type} />
      <button
        onClick={onExpand}
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: 0,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', gap: space[2], alignItems: 'center' }}>
          <span style={{ ...type.body, fontWeight: 600, color: colors.textPrimary }}>
            {events.length} {events[0].type === 'email' ? 'email activities' : `${events[0].type} events`}
          </span>
          <Icon category="utility" name="chevrondown" size="xx-small" style={{ fill: cfg.color }} />
        </div>
        <div style={{ ...type.micro, color: colors.textTertiary, marginTop: 2 }}>
          Click to expand
        </div>
      </button>
    </div>
  );
};

const UnifiedTimeline = ({ events = [], defaultCollapseType = true }) => {
  const [filters, setFilters] = useState(['all']);
  const [systemVisible, setSystemVisible] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const counts = useMemo(() => {
    const c = { all: events.filter(e => e.type !== 'system').length };
    events.forEach(ev => {
      if (ev.type === 'system') return;
      c[ev.type] = (c[ev.type] || 0) + 1;
    });
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    let list = events.slice();
    if (!systemVisible) list = list.filter(e => e.type !== 'system');
    if (!filters.includes('all')) list = list.filter(e => filters.includes(e.type));
    return list.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [events, filters, systemVisible]);

  // Group by day, then within-day group by type when >=3 of same type
  const grouped = useMemo(() => {
    const byDay = new Map();
    filtered.forEach(ev => {
      const day = dayBucket(ev.date);
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(ev);
    });
    const result = [];
    for (const [day, dayEvents] of byDay) {
      const typeGroups = new Map();
      dayEvents.forEach(ev => {
        if (!typeGroups.has(ev.type)) typeGroups.set(ev.type, []);
        typeGroups.get(ev.type).push(ev);
      });
      const items = [];
      for (const [evType, evs] of typeGroups) {
        if (defaultCollapseType && evs.length >= 3) {
          items.push({ kind: 'group', type: evType, events: evs });
        } else {
          evs.forEach(e => items.push({ kind: 'single', event: e }));
        }
      }
      result.push({ day, items });
    }
    return result;
  }, [filtered, defaultCollapseType]);

  // Separate system events for collapsed display
  const systemEvents = events.filter(e => e.type === 'system');

  return (
    <div>
      <TimelineFilterChips
        active={filters}
        onChange={setFilters}
        counts={counts}
        systemVisible={systemVisible}
        onToggleSystem={() => setSystemVisible(v => !v)}
      />

      {grouped.length === 0 && (
        <div style={{
          padding: `${space[6]}px ${space[4]}px`,
          textAlign: 'center',
          color: colors.textTertiary,
          ...type.body,
        }}>
          No events match the current filters.
        </div>
      )}

      {grouped.map(({ day, items }) => (
        <div key={day} style={{ marginBottom: space[5] }}>
          <div style={{
            ...type.label,
            color: colors.textTertiary,
            paddingBottom: space[2],
            marginBottom: space[3],
            borderBottom: `1px dashed ${colors.border}`,
          }}>
            {bucketLabel(day)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: space[4] }}>
            {items.map((item, idx) => {
              if (item.kind === 'single') {
                return <SingleItem key={item.event.id} event={item.event} />;
              }
              const gkey = `${day}-${item.type}`;
              const expanded = expandedGroups.has(gkey);
              if (!expanded) {
                return (
                  <CollapsedGroup
                    key={gkey}
                    events={item.events}
                    onExpand={() => setExpandedGroups(s => new Set([...s, gkey]))}
                  />
                );
              }
              return item.events.map(ev => <SingleItem key={ev.id} event={ev} />);
            })}
          </div>
        </div>
      ))}

      {!systemVisible && systemEvents.length > 0 && (
        <div style={{
          marginTop: space[6],
          padding: space[3],
          background: colors.surfaceInk,
          borderRadius: radius.sm,
          ...type.small,
          color: colors.textTertiary,
          textAlign: 'center',
        }}>
          {systemEvents.length} system event{systemEvents.length === 1 ? '' : 's'} hidden ·
          {' '}
          <button
            onClick={() => setSystemVisible(true)}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              color: colors.brand, cursor: 'pointer', fontFamily: 'inherit',
              ...type.small, fontWeight: 600, textDecoration: 'underline',
            }}
          >
            show
          </button>
        </div>
      )}
    </div>
  );
};

export default UnifiedTimeline;
